/**
 * 工人出工记录表单组件
 * 可复用的新增/编辑出工记录表单
 * 支持带班费：如果工人 isLeader=true，则可选择是否添加带班费
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  DatePicker,
  InputNumber,
  Select,
  AutoComplete,
  Button,
  Space,
  message,
  Checkbox,
  Card,
  Divider,
  Alert,
  Typography,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { WorkerService } from '@/services/workerService';
import { ProjectService } from '@/services/projectService';
import { useLeaders } from '@/modules/progress/hooks';
import WorkerAutoComplete from '@/components/WorkerAutoComplete';
import type { WorkerHistoryFormProps, WorkerHistoryFormValues } from './types';

const { TextArea } = Input;
const { Text } = Typography;

const CONSTRUCTION_TYPES = [
  { label: '园林', value: '园林' },
  { label: '土建', value: '土建' },
  { label: '外墙', value: '外墙' },
  { label: '其它', value: '其它' },
];

/**
 * 计算费用
 */
const calculateCosts = (
  workUnits: number,
  unitPrice: number,
  leaderFee: number,
  isLeader: boolean
) => {
  const baseTotalCost = workUnits * unitPrice;
  const leaderFeeTotal = isLeader ? workUnits * leaderFee : 0;
  const totalCost = baseTotalCost + leaderFeeTotal;
  return { baseTotalCost, leaderFeeTotal, totalCost };
};

/**
 * 工人出工记录表单组件
 * 用于新增或编辑出工记录
 */
const WorkerHistoryForm: React.FC<WorkerHistoryFormProps> = ({
  initialValues,
  defaultProjectId,
  onWorkerSelect,
  onWorkerClear,
  onSuccess,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [projectInputValue, setProjectInputValue] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(defaultProjectId);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | undefined>(undefined);
  const [selectedWorker, setSelectedWorker] = useState<any>(null);  // 保存选中的工人完整信息
  const [isLeadingWork, setIsLeadingWork] = useState(false);  // 本次是否带班
  const [leaderFee, setLeaderFee] = useState(0);  // 带班费
  const [workUnits, setWorkUnits] = useState(1);  // 工时
  const [unitPrice, setUnitPrice] = useState(0);  // 基础单价

  // 加载项目列表
  const { data: projectsData } = useQuery({
    queryKey: ['projects', 'active'],
    queryFn: () => ProjectService.getActiveProjects(),
  });

  // 加载负责人列表
  const { leaders, loading: leadersLoading } = useLeaders();

  // 判断当前工人是否有带班资格
  const workerCanLead = selectedWorker?.isLeader === true;

  // 初始化表单数据
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
      if (initialValues.projectName) {
        setProjectInputValue(initialValues.projectName);
      }
      // 编辑模式：设置带班相关状态
      if (initialValues.isLeader !== undefined) {
        setIsLeadingWork(initialValues.isLeader);
      }
      if (initialValues.leaderFee !== undefined) {
        setLeaderFee(initialValues.leaderFee);
      }
      if (initialValues.workUnits !== undefined) {
        setWorkUnits(initialValues.workUnits);
      }
      if (initialValues.unitPrice !== undefined) {
        setUnitPrice(initialValues.unitPrice);
      }
    } else {
      // 新增模式：设置默认值
      form.setFieldsValue({
        workDate: dayjs(),
        workUnits: 1,
        unitPrice: 0,
        leaderFee: 0,
        isLeader: false,
      });
      // 如果有默认项目 ID，预填充项目名称
      if (defaultProjectId) {
        const project = (projectsData?.data as any[] | undefined)?.find(
          (p: any) => p._id === defaultProjectId || p.id === defaultProjectId
        );
        if (project) {
          form.setFieldValue('projectName', project.name);
          setProjectInputValue(project.name);
        }
      }
    }
  }, [initialValues, defaultProjectId, form, projectsData]);

  // 监听工时和单价变化，自动计算费用
  useEffect(() => {
    const { baseTotalCost, leaderFeeTotal, totalCost } = calculateCosts(
      workUnits,
      unitPrice,
      leaderFee,
      isLeadingWork && workerCanLead
    );
    form.setFieldsValue({
      baseTotalCost,
      leaderFeeTotal,
      totalCost,
    });
  }, [workUnits, unitPrice, leaderFee, isLeadingWork, workerCanLead]);

  // 处理工人选择 - WorkerAutoComplete 返回完整工人对象
  const handleWorkerSelect = (worker: any) => {
    const workerId = worker._id || worker.id;
    setSelectedWorkerId(workerId);
    setSelectedWorker(worker);

    // 重置带班状态
    setIsLeadingWork(false);
    setLeaderFee(0);

    if (onWorkerSelect) {
      onWorkerSelect(workerId, worker);
    }
    // 如果工人有默认单价，自动填充
    if (worker?.defaultPrice !== undefined) {
      setUnitPrice(worker.defaultPrice);
      form.setFieldValue('unitPrice', worker.defaultPrice);
    }
  };

  // 处理工人清除
  const handleWorkerClear = () => {
    setSelectedWorkerId(undefined);
    setSelectedWorker(null);
    setIsLeadingWork(false);
    setLeaderFee(0);
    setUnitPrice(0);
    form.setFieldValue('unitPrice', 0);
    form.setFieldValue('leaderFee', 0);
    if (onWorkerClear) {
      onWorkerClear();
    }
  };

  // 处理项目选择
  const handleProjectSelect = (_val: string, option: any) => {
    if (option?.projectId) {
      setSelectedProjectId(option.projectId);
    }
  };

  // 处理项目变更
  const handleProjectChange = () => {
    setSelectedProjectId(undefined);
  };

  // 处理带班切换
  const handleLeaderToggle = (checked: boolean) => {
    setIsLeadingWork(checked);
    if (!checked) {
      setLeaderFee(0);
      form.setFieldValue('leaderFee', 0);
    }
  };

  // 表单提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // 只有工人有带班资格且勾选了带班时，leaderFee 才有效
      const effectiveIsLeader = isLeadingWork && workerCanLead;
      const effectiveLeaderFee = effectiveIsLeader ? leaderFee : 0;

      const { baseTotalCost, leaderFeeTotal, totalCost } = calculateCosts(
        workUnits,
        unitPrice,
        effectiveLeaderFee,
        effectiveIsLeader
      );

      const result: WorkerHistoryFormValues = {
        ...values,
        workDate: values.workDate || dayjs(),
        projectId: selectedProjectId,
        workerId: selectedWorkerId,
        isLeader: effectiveIsLeader,
        leaderFee: effectiveLeaderFee,
        baseTotalCost,
        leaderFeeTotal,
        totalCost,
      };
      onSuccess(result);
    } catch (error) {
      console.error('表单校验失败:', error);
    }
  };

  // 计算费用显示
  const effectiveIsLeader = isLeadingWork && workerCanLead;
  const { baseTotalCost, leaderFeeTotal, totalCost } = calculateCosts(
    workUnits,
    unitPrice,
    leaderFee,
    effectiveIsLeader
  );

  // 项目选项
  const projectOptions = (projectsData?.data as any[] || []).map((p: any) => ({
    value: p.name,
    label: p.name,
    projectId: p._id,
  }));

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        workDate: dayjs(),
        workUnits: 1,
        unitPrice: 0,
        leaderFee: 0,
        isLeader: false,
        baseTotalCost: 0,
        leaderFeeTotal: 0,
        totalCost: 0,
      }}
    >
      {/* 工作日期 */}
      <Form.Item
        label="工作日期"
        name="workDate"
        rules={[{ required: true, message: '请选择工作日期' }]}
      >
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      {/* 工人名字 */}
      <Form.Item
        label="工人名字"
        name="workerName"
        rules={[{ required: true, message: '请输入工人名字' }]}
      >
        <WorkerAutoComplete
          value={form.getFieldValue('workerName') || ''}
          placeholder="请输入工人姓名"
          onChange={(name) => form.setFieldValue('workerName', name)}
          onWorkerSelect={handleWorkerSelect}
          onClear={handleWorkerClear}
        />
      </Form.Item>

      {/* 工人带班状态提示 */}
      {selectedWorker && (
        <Form.Item name="workerInfo" noStyle>
          {workerCanLead ? (
            <Alert
              message={`${selectedWorker.name} 是班组长，可以选择是否添加带班费`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          ) : (
            <Alert
              message={`${selectedWorker.name} 是普通工人，只能按普通工价核算`}
              type="default"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
        </Form.Item>
      )}

      {/* 项目名称 */}
      <Form.Item
        label="项目名称"
        name="projectName"
        rules={[{ required: true, message: '请输入项目名称' }]}
      >
        <AutoComplete
          placeholder="请输入或选择项目名称"
          allowClear
          value={projectInputValue}
          onChange={(value) => setProjectInputValue(value)}
          filterOption={(input, option) =>
            (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onSelect={handleProjectSelect}
          onClear={handleProjectChange}
          options={projectOptions}
        />
      </Form.Item>

      {/* 工作内容 */}
      <Form.Item
        label="工作内容"
        name="workContent"
        rules={[{ required: true, message: '请输入工作内容' }]}
      >
        <TextArea rows={3} placeholder="请输入工作内容" />
      </Form.Item>

      {/* 工时（天） */}
      <Form.Item
        label="工时（天）"
        name="workUnits"
        rules={[{ required: true, message: '请输入工时' }]}
      >
        <InputNumber
          min={0.1}
          max={10}
          step={0.1}
          style={{ width: '100%' }}
          placeholder="请输入工时"
          onChange={(val) => setWorkUnits(val || 0)}
        />
      </Form.Item>

      {/* 单价（元） */}
      <Form.Item
        label="单价（元）"
        name="unitPrice"
        rules={[{ required: true, message: '请输入单价' }]}
      >
        <InputNumber
          min={0}
          style={{ width: '100%' }}
          placeholder="请输入单价"
          onChange={(val) => setUnitPrice(val || 0)}
        />
      </Form.Item>

      {/* 带班选项 - 仅班组长可见 */}
      {workerCanLead && (
        <>
          <Divider orientation="left">带班费设置</Divider>
          <Form.Item name="isLeader" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox onChange={(e) => handleLeaderToggle(e.target.checked)}>
              本次带班（添加带班费）
            </Checkbox>
          </Form.Item>

          {isLeadingWork && (
            <Form.Item
              label="带班费（元/天）"
              name="leaderFee"
              extra="班组长特有的额外费用，按工作天数计算"
            >
              <InputNumber
                min={0}
                step={10}
                style={{ width: '100%' }}
                placeholder="请输入带班费"
                onChange={(val) => setLeaderFee(val || 0)}
              />
            </Form.Item>
          )}
        </>
      )}

      {/* 费用汇总 - 仅带班时显示 */}
      {(effectiveIsLeader || workerCanLead) && (
        <>
          <Divider orientation="left">费用计算</Divider>
          <Card size="small" style={{ backgroundColor: '#fafafa' }}>
            <Space orientation="vertical" style={{ width: '100%' }} size="small">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>基础工资：</Text>
                <Text strong>{workUnits} 天 × {unitPrice} 元 = {baseTotalCost} 元</Text>
              </div>
              {effectiveIsLeader && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>带班费：</Text>
                  <Text type="warning">{workUnits} 天 × {leaderFee} 元 = {leaderFeeTotal} 元</Text>
                </div>
              )}
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>合计：</Text>
                <Text strong type={effectiveIsLeader ? 'success' : undefined}>
                  {totalCost} 元
                </Text>
              </div>
            </Space>
          </Card>
        </>
      )}

      {/* 施工类型 */}
      <Form.Item label="施工类型" name="constructionType">
        <Select allowClear placeholder="请选择施工类型" options={CONSTRUCTION_TYPES} />
      </Form.Item>

      {/* 负责人 */}
      <Form.Item label="负责人" name="leaderId">
        <Select
          placeholder="请选择负责人"
          allowClear
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={leaders.map(leader => ({
            label: leader.name,
            value: leader._id,
          }))}
          loading={leadersLoading}
        />
      </Form.Item>

      {/* 隐藏字段 */}
      <Form.Item name="baseTotalCost" noStyle>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="leaderFeeTotal" noStyle>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="totalCost" noStyle>
        <Input type="hidden" />
      </Form.Item>

      {/* 操作按钮 */}
      <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel}>取消</Button>
          <Button type="primary" onClick={handleSubmit}>
            保存
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default WorkerHistoryForm;
export type { WorkerHistoryFormProps, WorkerHistoryFormValues };
